<style lang="scss">
@import "resources/sass/v1/abstracts/_variables";

.job-form {
    position: relative;

    .error {
        position: relative !important;
        top: unset !important;
        right: unset !important;
    }

    input[type="file"] {
        display: none;
    }

    .filename {
        &:after {
            content: "x";
            margin-left: 8px;
            color: $black;
            font-weight: 700;
            opacity: 0;
            transition: opacity 0.2s;
        }

        &:hover {
            cursor: pointer;
            &:after {
                opacity: 1;
            }
        }
    }

    .label {
        font-size: 11px;
        line-height: 22px;
        display: inline-block;
        margin-bottom: 10px;
    }

    label.file-input {
        display: block;
        padding: 16px;
        width: 100%;
        border: 1px solid #dce0e4;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        color: rgba(53, 64, 82, 0.5);
        transition: border-color 0.2s;

        .blue {
            color: $primary;

            &:hover {
                cursor: pointer;
                text-decoration: underline;
            }
        }

        &.highlight {
            border-color: $primary;
        }
    }
}
</style>

<template>
    <div class="job-form" :class="{'processing': processing}">
        <div class="container-narrow">
            <h2 class="text-center h4 mb-40">{{ formTitle }}</h2>

            <div class="form" id="job-form" ref="form">
                <ValidationObserver v-slot="obs" ref="observer" tag="form" @submit.prevent="submit()">
                    <div class="form-row">
                        <div class="col-12 col-md-6">
                            <ValidationProvider ref="first_name" vid="first_name" rules="required|min:2" v-slot="props" :name="translations.first_name" :slim="true">
                                <div class="form-group position-relative" :class="props.classes">
                                    <label for="first_name">{{ translations.first_name }}</label>
                                    <input type="text" id="first_name" class="form-control" :placeholder="translations.first_name" v-model="formData.first_name" />
                                    <span class="error">{{ props.errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>

                        <div class="col-12 col-md-6">
                            <ValidationProvider ref="name" vid="name" rules="required|min:2" v-slot="props" :name="translations.name" :slim="true">
                                <div class="form-group position-relative" :class="props.classes">
                                    <label for="name">{{ translations.name }}</label>
                                    <input type="text" id="name" class="form-control" :placeholder="translations.name" v-model="formData.last_name" />
                                    <span class="error">{{ props.errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="col-12 col-md-6">
                            <ValidationProvider ref="email" vid="email" rules="required|email" v-slot="props" :name="translations.email" :slim="true">
                                <div class="form-group position-relative" :class="props.classes">
                                    <label for="email">{{ translations.email }}</label>
                                    <input type="text" id="email" class="form-control" :placeholder="translations.email" v-model="formData.email" />
                                    <span class="error">{{ props.errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>

                        <div class="col-12 col-md-6">
                            <ValidationProvider ref="phone" vid="phone" :rules="{ required: true, regex:/((?:\+|00)[17](?: |\-)?|(?:\+|00)[1-9]\d{0,2}(?: |\-)?|(?:\+|00)1\-\d{3}(?: |\-)?)?(0\d|\([0-9]{3}\)|[1-9]{0,3})(?:((?: |\-)[0-9]{2}){4}|((?:[0-9]{2}){4})|((?: |\-)[0-9]{3}(?: |\-)[0-9]{4})|([0-9]{7}))/}" v-slot="props" :name="translations.phone" :slim="true">
                                <div class="form-group position-relative" :class="props.classes">
                                    <label for="phone">{{ translations.phone }}</label>
                                    <input type="text" id="phone" class="form-control" :placeholder="translations.phone" v-model="formData.phone" />
                                    <span class="error">{{ props.errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="col-12">
                            <ValidationProvider ref="motivation" vid="motivation" rules="required|min:10" v-slot="props" :name="translations.motivation" :slim="true">
                                <div class="form-group position-relative" :class="props.classes">
                                    <label for="motivation">{{ translations.motivation }}</label>
                                    <textarea class="form-control flex-grow-1" id="motivation" :placeholder="translations.motivation" v-model="formData.motivation" />
                                    <span class="error error-top">{{ props.errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="col-12">
                            <div class="form-group position-relative">
                                <span class="label">CV</span>
                                <label id="drop-area" class="d-block file-input" for="cv" v-html="fileUploadText"></label>
                                <input type="file" id="cv" ref="cv" @change="handleFileUpload()"  accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf" />
                                <span v-if="formData.cv_file_name" class="filename" @click="clearUploads()">{{ formData.cv_file_name }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="col-12">
                            <ValidationProvider ref="privacy-policy" vid="privacy-policy" :rules="{ required: { allowFalse: false } }" v-slot="props" :name="translations.privacy_agreement.replace(/%s/g, '').replace(/\.$/, '')" :slim="true">
                                <div class="form-group position-relative" :class="props.classes">
                                    <input type="checkbox" id="privacy-policy" v-model="formData.privacyPolicy" />
                                    <label class="checkbox" :class="{'checked': formData.privacyPolicy}" for="privacy-policy" v-html="privacyPolicyLinkText"></label>
                                    <span class="error error-top">{{ props.errors[0] }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                    </div>

                    <span v-for="(error, index) in compiledErrors" class="error error-large mb-20 d-block" :key="index">{{ error }}</span>

                    <button @click.stop="submit(`/jobs/${jobId}/applications`)" :disabled="obs.invalid" class="btn btn-lg btn-secondary btn-special"><span>{{ translations.submit }}</span></button>
                </ValidationObserver>

                <div class="spinner-background">
                    <div class="spinner">

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import formBase from '@mixins/formBase'
export default {
    mixins: [formBase],

    props: {
        formTitle: {
            type: String,
            required: true,
        },

        jobId: {
            type: String,
            required: true,
        },

        translations: {
            type: Object,
            required: true,
        }
    },

    data() {
        return {
            formData: {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
                motivation: "",
                cv: "",
                cv_file_name: "",
                privacyPolicy: null,
            }
        }
    },

    mounted: function () {
        var self = this;

        let dropArea = document.getElementById('drop-area');
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropArea.addEventListener(eventName, preventDefaults, false)
        });

        function preventDefaults(e) {
            e.preventDefault()
            e.stopPropagation()
        }

        ['dragenter', 'dragover'].forEach(eventName => {
            dropArea.addEventListener(eventName, highlight, false)
        });

        ['dragleave', 'drop'].forEach(eventName => {
            dropArea.addEventListener(eventName, unhighlight, false)
        });

        function highlight(e) {
            dropArea.classList.add('highlight')
        }

        function unhighlight(e) {
            dropArea.classList.remove('highlight')
        }

        dropArea.addEventListener('drop', handleDrop, false)

        async function handleDrop(e) {
            let dt = e.dataTransfer
            let files = dt.files


            self.$refs.cv.files = files
            self.formData.cv = await self.toBase64(self.$refs.cv.files[0])
            self.formData.cv_file_name = self.$refs.cv.files[0].name
        }
    },

    computed: {
        fileUploadText: function () {
            var output = this.translations.curriculum_vitae_placeholder;

            if ((output.match(/%s/g) || []).length == 2) { //only replace if 2 %s tags are present in the string
                var i = 0;
                var spanReplacements = ["<span class='blue'>", "</span>"]
                output = output.replace(/%s/g, function (matched) {
                    return spanReplacements[i];
                    i++;
                });
            }

            return output;
        },

        privacyPolicyLinkText: function () {
            var output = this.translations.privacy_agreement;

            if ((output.match(/%s/g) || []).length == 2) { //only replace if 2 %s tags are present in the string
                var i = 0;
                var anchorReplacements = ["<a href=\"" + this.privacyPolicyLink + "\">", "</a>"]
                output = output.replace(/%s/g, function (matched) {
                    return anchorReplacements[i];
                    i++;
                });
            }

            return output;
        }
    },

    methods: {
        async handleFileUpload() {
            this.formData.cv = await this.toBase64(this.$refs.cv.files[0]);
            this.formData.cv_file_name = this.$refs.cv.files[0].name;
        },

        toBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = error => reject(error);
            })
        },

        clearUploads() {
            this.formData.cv = '';
            this.formData.cv_file_name = '';
            document.getElementById("cv").value = "";
        },

        async submit(path) {
            const isValid = await this.$refs.observer.validate();
            if (isValid && path != null && path != '') {
                this.processing = true;
                //prepare FormData object from our generic object
                var formData = new FormData();
                formData.append('first_name', this.formData.first_name);
                formData.append('last_name', this.formData.last_name);
                formData.append('email', this.formData.email);
                formData.append('phone', this.formData.phone);
                formData.append('motivation', this.formData.motivation);
                formData.append('cv', this.formData.cv);
                formData.append('cv_file_name', this.formData.cv_file_name);

                axios
                    .post(path, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                    .then((response) => {
                        if (response.data.hasOwnProperty('success')) this.submitted = true;
                        if (response.data.hasOwnProperty('success')) this.done = true;
                        this.clearFormFields(this.formData);

                        this.$refs.form.innerHTML = "";
                        var h5 = document.createElement('h5');
                        h5.innerHTML = this.translations.success_message;
                        this.$refs.form.appendChild(h5);
                    })
                    .catch((error) => {
                        this.errors = error.response.data.errors
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        }
    },
}
</script>
